/** One To/CC recipient on the proposal email. `contactId` is set when it is a Dynamics contact. */
export type EmailRecipient = {
  email: string
  contactId?: string
  name?: string
  firstName?: string
}

type RecipientContact = {
  contactid: string
  fullname?: string | null
  firstname?: string | null
  emailaddress1?: string | null
}

export function normalizeEmail(email: string) {
  return email.trim().toLowerCase()
}

/** A contact as a recipient; null when it has no email. */
export function recipientFromContact(contact: RecipientContact | null | undefined): EmailRecipient | null {
  const email = contact?.emailaddress1?.trim()
  if (!contact || !email) return null
  // Whitespace collapsed like the editor does, so the greeting stays matchable.
  const name = contact.fullname?.replace(/\s+/g, ' ').trim() || undefined
  const firstName = contact.firstname?.replace(/\s+/g, ' ').trim() || name?.split(' ')[0] || undefined
  return { email, contactId: contact.contactid, name, firstName }
}

/**
 * A typed email, matched to the one known contact with that email. A shared
 * (household) email matches no single person, so it stays a plain address.
 */
export function recipientFromEmail(email: string, knownContacts: RecipientContact[]): EmailRecipient {
  const key = normalizeEmail(email)
  const matches = knownContacts.filter((c) => c.emailaddress1 && normalizeEmail(c.emailaddress1) === key)
  return (matches.length === 1 ? recipientFromContact(matches[0]) : null) ?? { email: email.trim() }
}

/** The recipient as it is greeted once its address is saved onto `contact`. */
export function greetedAs(recipient: EmailRecipient, contact: RecipientContact | null | undefined): EmailRecipient {
  const named = recipientFromContact(contact ? { ...contact, emailaddress1: recipient.email } : null)
  return named ? { ...recipient, name: named.name, firstName: named.firstName } : recipient
}

const listFormat = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' })

/** "Ana" · "Ana and Ben" · "Ana, Ben, and Cy". */
export function formatList(items: string[]) {
  return listFormat.format(items)
}

// Text-node escaping only: tiptap's getHTML() escapes & < > in text but not
// quotes, so the greeting must be escaped the same way to stay matchable.
function escapeText(value: string) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * The greeting line for the To recipients (CC is not greeted):
 * "Hi Ana," · "Hi Ana and Ben," · "Hi Ana, Ben, and Cy," — "Hi there," for one
 * recipient without a name, and "Hi all," for more than three or for a group
 * that includes someone without a name.
 */
export function buildGreeting(to: EmailRecipient[]) {
  const names = to.map((recipient) => recipient.firstName?.trim()).filter((name): name is string => !!name)
  if (to.length === 0 || (to.length === 1 && names.length === 0)) return 'Hi there,'
  if (names.length !== to.length || names.length > 3) return 'Hi all,'
  return `Hi ${formatList(names)},`
}

/** The greeting as the HTML paragraph the email template starts with. */
export function greetingParagraph(to: EmailRecipient[]) {
  return `<p>${escapeText(buildGreeting(to))}</p>`
}

/**
 * Swaps the greeting for the new To list, but only while the body still has
 * the greeting generated for the previous list — a greeting the sender typed
 * themselves is left alone.
 */
export function retargetGreeting(body: string, previousTo: EmailRecipient[], nextTo: EmailRecipient[]) {
  const previous = greetingParagraph(previousTo)
  const next = greetingParagraph(nextTo)
  if (previous === next || !body.includes(previous)) return body
  return body.replace(previous, () => next)
}
